import { appIntl } from '@components/locale/IntlGlobalProvider';
import { BlastSectorOption, SectorOption } from '@components/map/popup/engagement/EngagementSelector';

import { SafetyKeyLight } from '@/types/authent/login.types';
import {
  BlastJammingCommand,
  Dfb6JammingCommand,
  MajesJammingCommand,
  Mc2CommandEnum,
  NeutralInfo,
} from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import {
  EXTENDED_NEUTRAL_SENSORS,
  NEUTRAL_SENSORS_WITH_SECTORS,
  NeutralConfigurationWithSectors,
  NeutralSensorWithSectorsType,
  SensorConfiguration,
} from '@/types/sensor/configuration.types';
import {
  ActiveMonitoringSensor,
  ActiveMonitoringSensorByType,
  InactiveMonitoringSensor,
  InactiveMonitoringSensorByType,
  MonitoringSensorByType,
  NeutralActiveMonitoringWithSectors,
} from '@/types/sensor/monitoring.types';
import {
  Dfb6Mode,
  Dfb6SensorStatus,
  ExtendedSensor,
  JammingModeEnum,
  JammingStatus,
  OutputMode,
  OutputModeValueType,
  SectorConfig,
  SectorConfigKey,
  SectorConfiguration,
  SensorTypeEnum,
  TrackingStatusEnum,
} from '@/types/sensor/sensors';
import { NeutralStatusWithSectors } from '@/types/sensor/status.types';

import { isMonitoringSensorOfType, isSensorOrSubSensorConfigurationOfType } from '../sensors.utils';

//This function is mostly used to determine if a sensor should appear red when emitting instead of orange
export function isNeutral(type: ExtendedSensor): boolean {
  return EXTENDED_NEUTRAL_SENSORS.some((t) => t === type);
}

export function getContextMenuNeutralLabel(neutralMonitoring: ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>) {
  const formatMessage = appIntl().formatMessage;
  if (neutralMonitoring.status.trackingStatus.globalSensorTrackingState === TrackingStatusEnum.ACTIVE) {
    return formatMessage({ id: 'contextmenu.label.inTracking' }, { name: neutralMonitoring.configuration.name });
  }
  return neutralMonitoring.configuration.name;
}

export function isNeutralActiveMonitoringSensorWithSectors(
  monitoringSensor: ActiveMonitoringSensor,
): monitoringSensor is ActiveMonitoringSensorByType<NeutralSensorWithSectorsType> {
  return NEUTRAL_SENSORS_WITH_SECTORS.some((type) => isMonitoringSensorOfType(monitoringSensor, type));
}

export function isNeutralInactiveMonitoringSensorWithSectors(
  monitoringSensor: InactiveMonitoringSensor,
): monitoringSensor is InactiveMonitoringSensorByType<NeutralSensorWithSectorsType> {
  return NEUTRAL_SENSORS_WITH_SECTORS.some((type) => isMonitoringSensorOfType(monitoringSensor, type));
}

export function isNeutralSensorConfigurationWithSectors(
  sensor: SensorConfiguration,
): sensor is NeutralConfigurationWithSectors {
  return NEUTRAL_SENSORS_WITH_SECTORS.some((type) => isSensorOrSubSensorConfigurationOfType(sensor, type));
}

export function getSelectedMode(
  monitoring: ActiveMonitoringSensorByType<SensorTypeEnum.DFB6 | SensorTypeEnum.BLAST>,
): OutputModeValueType | null {
  return monitoring.status.selectedMode;
}

export function getSelectedSector(monitoring: ActiveMonitoringSensorByType<SensorTypeEnum.MAJES>): number | null {
  return monitoring.status.selectedSector;
}

export function isMajes(
  monitoring: MonitoringSensorByType<SectorConfigKey>,
): monitoring is ActiveMonitoringSensorByType<SensorTypeEnum.MAJES> {
  return isMonitoringSensorOfType(monitoring, SensorTypeEnum.MAJES);
}

export function isDfb6(
  monitoring: MonitoringSensorByType<SectorConfigKey>,
): monitoring is MonitoringSensorByType<SensorTypeEnum.DFB6> {
  return isMonitoringSensorOfType(monitoring, SensorTypeEnum.DFB6);
}

export function isBlast(
  monitoring: MonitoringSensorByType<SectorConfigKey>,
): monitoring is MonitoringSensorByType<SensorTypeEnum.BLAST> {
  return isMonitoringSensorOfType(monitoring, SensorTypeEnum.BLAST);
}

export function getSelectedId(monitoring: NeutralActiveMonitoringWithSectors): number | null {
  if (isMajes(monitoring)) {
    return getSelectedSector(monitoring);
  } else if (isDfb6(monitoring) || isBlast(monitoring)) {
    return getSelectedMode(monitoring);
  }
  return null;
}

export function getSectorId(sector: SectorConfig): number {
  return 'sectorId' in sector ? sector.sectorId : sector.modeId;
}

export function getSectorConfigurations<T extends NeutralSensorWithSectorsType>(
  monitoring: MonitoringSensorByType<T>,
): SectorConfiguration<T>[] {
  if (isMajes(monitoring)) {
    return monitoring.configuration.sectors as SectorConfiguration<T>[];
  } else if (isDfb6(monitoring) || isBlast(monitoring)) {
    return monitoring.configuration.modes as SectorConfiguration<T>[];
  }
  return [];
}

export function getSelectedSectorConfiguration<T extends NeutralSensorWithSectorsType>(
  monitoring: ActiveMonitoringSensorByType<T>,
): SectorConfiguration<T> | null {
  const selectedId = getSelectedId(monitoring);
  if (selectedId === null) {
    return null;
  }
  return getSectorConfigurations(monitoring).find((sector) => getSectorId(sector) === selectedId) ?? null;
}

export function getNotSelectedSectorConfiguration<T extends NeutralSensorWithSectorsType>(
  monitoring: ActiveMonitoringSensorByType<T>,
): SectorConfiguration<T>[] {
  const selectedId = getSelectedId(monitoring);
  const sectors = getSectorConfigurations(monitoring);
  if (selectedId === null) {
    return sectors;
  }
  return sectors.filter((sector) => getSectorId(sector) !== selectedId);
}

export function isJamming(status: NeutralStatusWithSectors | null): boolean {
  if (status === null) {
    return false;
  }
  return status.jammingStatus !== JammingStatus.OFF;
}

export function getNeutralSensorModuleEnabledStates(sensor: NeutralConfigurationWithSectors): Record<number, boolean> {
  return sensor.modules
    .filter((m) => m.active === true)
    .reduce((acc, curr) => ({ ...acc, [curr.moduleId]: curr.enabled }), {} as Record<number, boolean>);
}

export function noOperationalModules(sensor: NeutralConfigurationWithSectors): boolean {
  return sensor.modules.every((m) => !(m.active && m.enabled));
}

export function isCharge(config: SectorConfig): boolean {
  return 'modeId' in config ? config.mode === OutputMode.CHARGE : false;
}

const typeOrder = [OutputMode.DIRECTIVE, 'MajesSector', OutputMode.OMNI, OutputMode.CHARGE];

export function sortByType(configA: SectorConfig, configB: SectorConfig): number {
  const indexA = typeOrder.indexOf('modeId' in configA ? configA.mode : 'MajesSector');
  const indexB = typeOrder.indexOf('modeId' in configB ? configB.mode : 'MajesSector');
  return indexA - indexB;
}

export function sortSectorOption(optionA: SectorOption, optionB: SectorOption): number {
  const isChargeA = isCharge(optionA.sectorConfig);
  const isChargeB = isCharge(optionB.sectorConfig);
  if (isChargeA && !isChargeB) {
    return 1;
  } else if (isChargeB && !isChargeA) {
    return -1;
  }
  if (optionA.contains !== optionB.contains) {
    return optionA.contains ? -1 : 1;
  }
  if (optionA.dist !== optionB.dist) {
    return optionA.dist - optionB.dist;
  }
  return sortByType(optionA.sectorConfig, optionB.sectorConfig);
}

export function isPositionEngaged(neutralInfo: NeutralInfo | null): boolean {
  return !!neutralInfo?.coordinates;
}

export function getNeutralInfoCoordinates(neutralInfo: NeutralInfo | null): Coordinates | null {
  return neutralInfo?.coordinates ?? null;
}

export function isTargetEngaged(neutralInfo: NeutralInfo | null, targetId: string): boolean {
  return neutralInfo?.targetId === targetId;
}

export function isDbf6Mode(sector: SectorConfig): sector is Dfb6Mode {
  return 'fox' in sector;
}

export function getSectorName(sector: SectorConfig): string {
  if (isDbf6Mode(sector)) {
    return `${sector.name} (${sector.mode})`;
  }
  return sector.name;
}

export function isWrongFox(sectorOption: SectorOption | undefined, sectorOptions: SectorOption[]) {
  if (sectorOption === undefined) {
    return false;
  }
  const config = sectorOption.sectorConfig;
  if (!isDbf6Mode(config)) {
    return false;
  } else {
    const sector = sectorOptions.find((sectOpt) => sectOpt.value === sectorOption.value);
    const statusFox = (sector?.monitoring.status as Dfb6SensorStatus).currentConfiguration;
    return config.fox !== statusFox;
  }
}

export function isBlastConfigSet(sector: BlastSectorOption): boolean {
  const availableConfigs = sector.monitoring.configuration.configs;
  const selectedConfig = sector.monitoring.status.selectedConfigId;
  return availableConfigs.map((c) => c.configId).includes(selectedConfig);
}

export function getSafetyKeyColor(safetyKey: SafetyKeyLight | null) {
  if (safetyKey?.active !== true) {
    return 'neutral.300';
  } else if (safetyKey.locked) {
    return 'red.500';
  } else {
    return 'green.500';
  }
}

export function getJammingCommand(
  selectedSector: SectorOption,
  commandType: Mc2CommandEnum,
  blastForceActive: boolean,
) {
  const command = {
    type: commandType,
    sensorCode: selectedSector.monitoring.configuration.code,
    admin: false,
    forced: false,
  };

  switch (commandType) {
    case Mc2CommandEnum.MajesJamming:
      (command as MajesJammingCommand).sectorMode = getSectorId(selectedSector.sectorConfig);
      (command as MajesJammingCommand).modules = getNeutralSensorModuleEnabledStates(
        selectedSector.monitoring.configuration,
      );
      break;
    case Mc2CommandEnum.Dfb6Jamming:
      (command as Dfb6JammingCommand).sectorMode = getSectorId(selectedSector.sectorConfig);
      (command as Dfb6JammingCommand).modules = getNeutralSensorModuleEnabledStates(
        selectedSector.monitoring.configuration,
      );
      break;
    case Mc2CommandEnum.BlastJamming:
      (command as BlastJammingCommand).jammingMode = blastForceActive ? JammingModeEnum.ACTIVE : JammingModeEnum.HYBRID;
      break;
    case Mc2CommandEnum.StopJamming:
      break;
    default:
      console.error('Unknown jamming command', commandType);
      break;
  }

  return command;
}
